// ABOUTME: Pure helper enforcing the day-assignment invariant for Order Intake / Cancellation Lead Time forms:
// ABOUTME: applicableDays = businessDays minus nonStandard days; nonStandard entries stay within business days.

import {DAYS, DayIdentifier} from '../types/deliveryWindow'
import {
  NonStandardLeadTimeFormValues,
  OrderIntakeLeadTimeFormValues,
  StandardLeadTimeFormValues,
  fromDayIdentifier,
  toDayIdentifier,
} from '../types/orderIntakeLeadTime'
import {pruneNonBusinessDays} from './pruneNonBusinessDays'

export interface NormalizeLeadTimeDaysResult {
  changed: boolean
  standard: StandardLeadTimeFormValues | null
  nonStandard: NonStandardLeadTimeFormValues[]
}

/**
 * Normalizes the day assignments of lead time form values against the resolved business days:
 * 1. Drops non-business days from `standard.applicableDays` and `nonStandard` (see
 *    {@link pruneNonBusinessDays}).
 * 2. Drops `nonStandard` entries whose day is also in `applicableDays` (the standard
 *    assignment wins on conflicting data). Days are compared by identifier, so mixed
 *    code/full-name formats still conflict.
 * 3. Adds business days covered by neither list to `applicableDays`, preserving the
 *    day format (codes vs full names) used by the incoming values.
 *
 * Non-standard entries on business days are explicit overrides and are always preserved.
 * Returns `changed: false` (and the original references) when nothing needs normalizing.
 */
export const normalizeLeadTimeDays = (
  values: Pick<OrderIntakeLeadTimeFormValues, 'standard' | 'nonStandard'>,
  nonBusinessDays: DayIdentifier[],
): NormalizeLeadTimeDaysResult => {
  const sampleDay = values.standard?.applicableDays[0] ?? values.nonStandard[0]?.day
  const usesCodes = sampleDay ? sampleDay.length <= 3 && sampleDay === sampleDay.toUpperCase() : false

  const pruned = pruneNonBusinessDays(values, nonBusinessDays)
  let {changed, standard, nonStandard} = pruned

  if (!standard) {
    return {changed, standard, nonStandard}
  }

  const applicableDayIds = new Set(
    standard.applicableDays
      .map(toDayIdentifier)
      .filter((d): d is DayIdentifier => d !== undefined),
  )

  const conflictFree = nonStandard.filter((ns) => {
    const id = toDayIdentifier(ns.day)
    return id === undefined || !applicableDayIds.has(id)
  })
  if (conflictFree.length !== nonStandard.length) {
    nonStandard = conflictFree
    changed = true
  }

  const nonBusinessSet = new Set(nonBusinessDays)
  const nonStandardDayIds = new Set(
    nonStandard
      .map((ns) => toDayIdentifier(ns.day))
      .filter((d): d is DayIdentifier => d !== undefined),
  )
  const missing = DAYS.filter(
    (d) => !nonBusinessSet.has(d) && !applicableDayIds.has(d) && !nonStandardDayIds.has(d),
  )
  if (missing.length > 0) {
    standard = {
      ...standard,
      applicableDays: [
        ...standard.applicableDays,
        ...missing.map((id) => fromDayIdentifier(id, usesCodes)),
      ],
    }
    changed = true
  }

  return {changed, standard, nonStandard}
}
